import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, IconButton, Dialog, DialogContent } from '@mui/material';
import CloseIcon        from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useForm, ValidationError } from '@formspree/react';
import { C, F, SH } from '../styles/tokens';
import { fieldSx, btnBlueSx } from '../styles/mixins';

const MOBILE_RE = /^[6-9][0-9]{9}$/;
const EMPTY     = { name: '', email: '', mobile: '', city: '', message: '' };

/**
 * Reusable consultation form modal.
 * Used wherever a CTA button should open the booking form.
 *
 * Props:
 *   open   {boolean}  – controlled open state
 *   onClose {function} – callback to close
 */
const ConsultationModal = ({ open, onClose }) => {
  const [state, handleSubmit] = useForm('mnnpwlwq');
  const [fields, setFields]   = useState(EMPTY);
  const [status, setStatus]   = useState(null);

  const handleChange = (e) =>
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  useEffect(() => {
    if (state.succeeded) {
      setStatus('success');
      setFields(EMPTY);
      const t = setTimeout(() => { setStatus(null); onClose(); }, 3000);
      return () => clearTimeout(t);
    }
    if (state.errors?.length) setStatus('error');
  }, [state.succeeded, state.errors, onClose]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!MOBILE_RE.test(fields.mobile)) {
      alert('Please enter a valid 10-digit mobile number starting with 6–9');
      return;
    }
    handleSubmit(e);
  };

  // Reset status when reopened
  useEffect(() => { if (open) setStatus(null); }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow:    SH.modal,
          px:           { xs: 0.5, sm: 1 },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: 'absolute', top: 12, right: 12, color: C.textLight }}
        >
          <CloseIcon />
        </IconButton>

        <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: 20, color: C.navy, mb: 0.5 }}>
          Book Free Consultation
        </Typography>
        <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMuted, mb: 2.5 }}>
          Our experts will reach out within 24 hours.
        </Typography>

        <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField label="Your Name"           name="name"    fullWidth required value={fields.name}    onChange={handleChange} sx={fieldSx} />
          <TextField label="Email Address"       name="email"   type="email" fullWidth required value={fields.email}   onChange={handleChange} sx={fieldSx} />
          <ValidationError prefix="Email" field="email" errors={state.errors} />

          <TextField
            label="Mobile Number"
            name="mobile"
            type="tel"
            fullWidth
            required
            value={fields.mobile}
            onChange={handleChange}
            sx={fieldSx}
            error={!!fields.mobile && !MOBILE_RE.test(fields.mobile)}
            helperText={fields.mobile && !MOBILE_RE.test(fields.mobile) ? 'Enter valid 10-digit number (starts 6–9)' : ''}
          />

          <TextField label="Your City"           name="city"    fullWidth required value={fields.city}    onChange={handleChange} sx={fieldSx} />
          <TextField label="Message (optional)"  name="message" multiline rows={2} fullWidth value={fields.message} onChange={handleChange} sx={fieldSx} />

          {status === 'success' && (
            <Box sx={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px', p: 1.5 }}>
              <Typography sx={{ fontFamily: F.body, color: '#065F46', fontSize: 13, fontWeight: 500 }}>
                ✓ Thanks! We'll get back to you within 24 hours.
              </Typography>
            </Box>
          )}
          {status === 'error' && (
            <Typography sx={{ fontFamily: F.body, color: '#DC2626', fontSize: 13 }}>
              Something went wrong. Please try again.
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={state.submitting}
            endIcon={<ArrowForwardIcon />}
            sx={{ ...btnBlueSx, mt: 0.5, py: 1.5, fontSize: 15, '&:disabled': { opacity: 0.6 } }}
          >
            Book Now
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationModal;
